import { Directive, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  getContracts,
  getContractsActionState,
  getSelectedContract,
  getTheme,
  LoadContracts,
} from '../../+state';
import {
  EnhancedContract,
  HexColorCode,
  IChartData,
  IChartDataItem,
  IContract,
  ImmomioIconName,
  ITheme,
  LoadingType,
  PaymentType,
} from '../../models';
import { getColorsVariations } from '../../utils';

const paymentKeys = [
  { target: PaymentType.RENT, translateKey: 'contract.payment.type.rent' },
  {
    target: PaymentType.HEATING_PREPAYMENT,
    translateKey: 'contract.payment.type.heating_prepayment',
  },
  {
    target: PaymentType.SERVICE_PAYMENT,
    translateKey: 'contract.payment.type.service_payment',
  },
  {
    target: PaymentType.PARKING,
    translateKey: 'garage_parking_space',
  },
  {
    target: PaymentType.OTHER,
    translateKey: 'contract.payment.type.others',
  },
  {
    target: PaymentType.TOTAL_RENT,
    translateKey: 'service.total_rent_l',
  },
];

@Directive()
export class ContractsBaseComponent {
  public selectedContractIndex: number;
  public immomioIconName = ImmomioIconName;
  public allContracts: EnhancedContract[] = [];
  public loadingTypes: LoadingType[] = [];
  public store = inject(Store);
  protected contracts$: Observable<IContract[]> = this.store.select(getContracts);
  protected theme$: Observable<ITheme> = this.store.select(getTheme);
  protected selectedContract$ = this.store.select(getSelectedContract);
  protected contractsActionState$ = this.store.select(getContractsActionState);

  /**
   * DEPRECATED
   * Prepare chart data
   * @param contract
   * @param primaryColor
   * @param secondaryColor
   */
  public prepareChartData(contract: IContract, primaryColor, secondaryColor): IChartData {
    const colors: HexColorCode[] = getColorsVariations(
      primaryColor,
      secondaryColor,
      contract.payments.length
    );
    const set: IChartDataItem[] = contract.payments.map((obj, i) => {
      let label = obj.type.toString();
      const amount = obj.cost;

      // We check, if we only have one position given. If that's the case, we need show the title of the legend accordingly
      // TODO: revert the check, when we have a better solution
      // ART-13689

      if (contract.payments.length === 1 && label === PaymentType.RENT) {
        label = paymentKeys.find(pk => pk.target == PaymentType.TOTAL_RENT).translateKey;
      } else {
        const paymentKey = paymentKeys.find(pk => pk.target == obj.type);
        if (paymentKey) {
          label = paymentKey.translateKey;
        }
      }
      return { label, amount, color: colors[i] };
    });

    set.sort((a, b) => {
      const aIndex = paymentKeys.findIndex(key => key.translateKey === a.label);
      const bIndex = paymentKeys.findIndex(key => key.translateKey === b.label);
      return aIndex - bIndex;
    });

    return {
      label: contract.externalId,
      set: set,
    };
  }

  /**
   * Prepare chart data detail costs
   * @param contract
   * @param primaryColor
   * @param secondaryColor
   */
  public prepareChartDataDetailCosts(
    contract: IContract,
    primaryColor: string,
    secondaryColor: string
  ): IChartData {
    const colors: HexColorCode[] = getColorsVariations(
      primaryColor,
      secondaryColor,
      contract.detailPayments.length
    );
    const set: IChartDataItem[] = contract.detailPayments.map((obj, i) => {
      let label = obj.type.toString();
      const amount = obj.total;

      // We check, if we only have one position given. If that's the case, we need show the title of the legend accordingly
      // TODO: revert the check, when we have a better solution
      // ART-13689

      if (contract.detailPayments.length === 1 && label === PaymentType.RENT) {
        label = paymentKeys.find(pk => pk.target == PaymentType.TOTAL_RENT).translateKey;
      } else {
        const paymentKey = paymentKeys.find(pk => pk.target == obj.type);
        if (paymentKey) {
          label = paymentKey.translateKey;
        }
      }
      return { label, amount, color: colors[i], details: obj.details };
    });

    set.sort((a, b) => {
      const aIndex = paymentKeys.findIndex(key => key.translateKey === a.label);
      const bIndex = paymentKeys.findIndex(key => key.translateKey === b.label);
      return aIndex - bIndex;
    });

    return {
      label: contract.externalId,
      set: set,
    };
  }

  public calculateRent(sets: IChartDataItem[]): number {
    return sets.reduce((acc, prev) => acc + prev.amount, 0);
  }

  protected loadContracts(forced: boolean): void {
    this.store.dispatch(LoadContracts({ forced: forced }));
  }
}
